import { A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { markRaw } from 'vue';
import 'swiper/css';
import VideoView from '../video-view/video-view.vue';
import { loadVideoMetadata } from './video-metadata';
import {
  normalizeVideos,
  resolveVideoHeader,
  resolveVideoMaxHeight,
  videoCategoryTone,
  videoLayout,
  videoId,
} from './video-model';

export default {
  components: { VideoView, Swiper, SwiperSlide },
  props: {
    widget: { type: Object, default: () => ({}) },
    order: { type: String, default: '' },
    maxHeight: { type: [String, Number], default: null },
    trackingContext: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      selectedIndex: 0,
      selectionVersion: 0,
      width: 0,
      containerHeight: null,
      headerHeight: 0,
      metadata: {},
      failedImages: {},
      swiper: null,
      swiperModules: [A11y],
      canPrevious: false,
      canNext: false,
    };
  },
  computed: {
    videos() {
      return normalizeVideos(this.widget, this.order);
    },
    activeVideo() {
      return this.videos[this.selectedIndex];
    },
    header() {
      if (!this.activeVideo) return null;
      return resolveVideoHeader(
        this.activeVideo,
        this.metadata[this.metadataKey(this.activeVideo)]?.title,
      );
    },
    categoryTone() {
      return videoCategoryTone(this.header?.category);
    },
    selectionKey() {
      return `${this.selectionVersion}:${this.selectedIndex}`;
    },
    slidesPerView() {
      return this.width < 600 ? 2 : 4;
    },
    heightLimit() {
      return resolveVideoMaxHeight(this.maxHeight ?? this.widget.maxHeight, this.containerHeight);
    },
    layout() {
      return videoLayout(
        this.width,
        this.heightLimit,
        this.videos.length > 1,
        this.slidesPerView,
        this.headerHeight,
      );
    },
    widgetStyle() {
      return {
        maxHeight: this.heightLimit == null ? undefined : `${this.heightLimit}px`,
      };
    },
    playerStyle() {
      return this.width
        ? { width: `${this.layout.playerWidth}px`, height: `${this.layout.playerHeight}px` }
        : { width: '100%', aspectRatio: '16 / 9' };
    },
    rowStyle() {
      // The scaled gap sits above the row only; the header has its own fixed spacing.
      return {
        width: `${this.layout.playerWidth}px`,
        height: `${this.layout.rowHeight}px`,
        marginTop: `${this.layout.gap}px`,
      };
    },
  },
  watch: {
    videos: {
      immediate: true,
      handler(videos) {
        this.selectedIndex = 0;
        this.selectionVersion += 1;
        for (const video of videos) {
          loadVideoMetadata(video).then((data) => {
            if (!this.disposed) this.metadata[this.metadataKey(video)] = data;
          });
        }
        this.$nextTick(() => {
          this.swiper?.slideTo(0, 0);
          this.updateNavigation();
        });
      },
    },
    maxHeight() {
      this.measure();
    },
    // A new title can wrap to a different number of lines; the observer re-measures.
    header() {
      this.$nextTick(() => {
        for (const element of this.headerElements()) this.resizeObserver?.observe(element);
        this.measure();
      });
    },
    slidesPerView() {
      this.$nextTick(() => {
        this.swiper?.update();
        this.updateNavigation();
      });
    },
  },
  methods: {
    metadataKey(video) {
      return `${video.source}:${video.videoUrl}`;
    },
    titleFor(video) {
      return (
        (typeof video.pseudoTitle === 'string' ? video.pseudoTitle.trim() : '') ||
        this.metadata[this.metadataKey(video)]?.title?.trim() ||
        ''
      );
    },
    thumbnailFor(video) {
      if (this.failedImages[this.metadataKey(video)]) return '';
      return (
        this.metadata[this.metadataKey(video)]?.thumbnail ||
        (video.source === 1 ? `https://i.ytimg.com/vi/${videoId(video)}/hqdefault.jpg` : '')
      );
    },
    imageFailed(video) {
      this.failedImages[this.metadataKey(video)] = true;
    },
    selectVideo(index) {
      if (index !== this.selectedIndex) this.selectedIndex = index;
    },
    revealCard(index) {
      if (
        this.swiper &&
        (index < this.swiper.activeIndex || index >= this.swiper.activeIndex + this.slidesPerView)
      )
        this.swiper.slideTo(index);
    },
    onSwiper(swiper) {
      this.swiper = markRaw(swiper);
      this.updateNavigation();
    },
    updateNavigation() {
      this.canPrevious = !!this.swiper && !this.swiper.isBeginning;
      this.canNext = !!this.swiper && !this.swiper.isEnd;
    },
    headerElements() {
      return [this.$refs.meta, this.$refs.heading].filter(Boolean);
    },
    measureHeaderHeight() {
      // Include the margins of every unscaled text element in the height limit.
      return this.headerElements().reduce((total, element) => {
        const style = getComputedStyle(element);
        return (
          total +
          element.getBoundingClientRect().height +
          (parseFloat(style.marginTop) || 0) +
          (parseFloat(style.marginBottom) || 0)
        );
      }, 0);
    },
    measure() {
      if (!this.$el || this.disposed) return;
      this.width = this.$el.getBoundingClientRect().width;
      this.headerHeight = this.measureHeaderHeight();
      const container =
        this.$el.closest('.predictit__videowidget')?.parentElement || this.$el.parentElement;
      if (!container) return;
      // Let CSS resolve 100% against the containing block. In an auto-height block
      const probe = document.createElement('div');
      probe.style.cssText =
        'all:initial;display:block;box-sizing:border-box;width:0;height:100%;min-height:0;flex:none;visibility:hidden;pointer-events:none;';
      container.appendChild(probe);
      const height = probe.getBoundingClientRect().height;
      probe.remove();
      this.containerHeight = height > 0 ? height : null;
    },
  },
  mounted() {
    this.measure();
    this.resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = requestAnimationFrame(() => this.measure());
    });
    this.resizeObserver.observe(this.$el);
    for (const element of this.headerElements()) this.resizeObserver.observe(element);
    const container =
      this.$el.closest('.predictit__videowidget')?.parentElement || this.$el.parentElement;
    if (container) this.resizeObserver.observe(container);
  },
  beforeUnmount() {
    this.disposed = true;
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.measureFrame);
  },
};
